import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import PageSeo from "@/components/feature/PageSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TipForm from "./components/TipForm";
import { channels } from "@/mocks/news";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "多方核实",
    text: "每条内容至少经过两个独立信源交叉核对，无法确认的细节宁可不写，也不用猜测填补。",
  },
  {
    icon: "ri-links-line",
    title: "标注信源",
    text: "正文中明确标注信息来源类型与回应状态，让读者自己判断可信度，而不是被动接受结论。",
  },
  {
    icon: "ri-lock-line",
    title: "尊重隐私",
    text: "不公开与事件无关的个人信息，不传播涉及未成年人隐私的材料，不鼓励任何形式的围观骚扰。",
  },
  {
    icon: "ri-refresh-line",
    title: "接受更正",
    text: "当事方提出更正申请后，编辑部在三个工作日内完成复核，确认有误即更正或撤下相关段落。",
  },
];

const milestones = [
  { year: "起步", title: "从一条线索开始", text: "最初只是一个整理行业线索的小栏目，每天更新两三条，靠读者口口相传积累起第一批关注。" },
  { year: "成型", title: "今日发布频道上线", text: "确立每天两次固定更新的节奏，把头条新闻、最新动态与今日聚焦拆分到不同栏目，信息层级更清楚。" },
  { year: "拓展", title: "五个频道并行", text: "陆续开设娱乐爆料、明星动态、影视综艺、网络热议与社会热点五个频道，覆盖更完整的资讯场景。" },
  { year: "现在", title: "把核实放在前面", text: "建立信源标注与更正机制，把「这条能不能发」的判断标准，从流量换成能否经得起回看。" },
];

const testimonials = [
  {
    name: "林女士",
    role: "长期读者",
    text: "别的地方看图说话，这里至少会写清楚哪些是已回应、哪些还在等回应，看起来踏实很多。",
  },
  {
    name: "周先生",
    role: "内容从业者",
    text: "今日聚焦那一条条关键词很好用，早上花五分钟就能把行业里发生的事过一遍。",
  },
  {
    name: "阿呓",
    role: "在校学生",
    text: "排版舒服，手机上刷起来不累。我最喜欢编辑推荐那一栏，基本不会踩雷。",
  },
];

const aboutImage =
  "https://readdy.ai/api/search-image?query=abstract%20warm%20editorial%20workspace%20with%20stacked%20cream%20newspapers%2C%20brass%20magnifier%20and%20soft%20morning%20light%20on%20a%20wooden%20desk%2C%20no%20people%2C%20muted%20warm%20palette%2C%20calm%20minimal%20still%20life%2C%20high%20detail%20magazine%20photography&width=1200&height=800&seq=bl-about-01&orientation=landscape";

export default function AboutPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <PageSeo
        title="关于我们 | 爆料官网 · 今日发布频道"
        description="了解爆料官网的网站定位、内容原则与更正机制。我们坚持多方核实、标注信源、尊重隐私，并长期接受读者的线索投稿与更正申请。"
        keywords="关于爆料官网,爆料官网内容原则,今日发布频道,线索投稿,更正机制"
        canonicalPath="/about"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于我们 | 爆料官网",
            url: "https://byfju.cn/about",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "爆料官网",
            url: "https://byfju.cn/",
            email: "tips@baoliao-today.example.com",
            description: "专注娱乐爆料与热点资讯的中文资讯站。",
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "编辑部",
              email: "tips@baoliao-today.example.com",
              availableLanguage: "zh-CN",
            },
          },
        ]}
      />
      <SiteHeader />
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <main className="flex-1">
        <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14" aria-labelledby="about-title">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                <span className="h-3 w-1 rounded-full bg-primary-500"></span>
                About Us
              </p>
              <h1 id="about-title" className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[42px]">
                把每一条爆料，
                <br className="hidden sm:block" />
                都放在可以回看的位置
              </h1>
              <p className="mt-5 max-w-xl font-body text-[15px] leading-8 text-foreground-600">
                爆料官网是一个专注娱乐爆料与热点资讯的中文资讯站。今日发布频道每天固定更新两次，
                汇总头条新闻、最新动态与今日聚焦内容。我们不追求最快，但希望每一条发出去的内容，
                在几天之后回头看，依然站得住。
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/channel/all"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  浏览全部爆料
                </Link>
                <a
                  href="#submit"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  我要投稿
                </a>
              </div>
            </div>
            <div className="h-64 w-full overflow-hidden rounded-lg md:h-80">
              <img
                src={aboutImage}
                alt="爆料官网编辑部整理的纸质资料与阅读工具"
                title="爆料官网 - 关于我们"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-10 md:py-14" aria-labelledby="position-heading">
          <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
            <h2 id="position-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              网站定位
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-newspaper-line text-xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">资讯汇编者</h3>
                <p className="mt-2 font-body text-sm leading-7 text-foreground-600">
                  我们不生产结论，只把分散在各处的公开信息整理成可读的脉络，让读者用更少时间了解一件事的全貌。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-user-voice-line text-xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">面向普通读者</h3>
                <p className="mt-2 font-body text-sm leading-7 text-foreground-600">
                  行文不堆术语，重要信息用短句讲清楚。移动端优先的排版，通勤路上也能顺畅读完一条内容。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-scales-3-line text-xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">保持克制</h3>
                <p className="mt-2 font-body text-sm leading-7 text-foreground-600">
                  热度不是唯一标准。涉及个人隐私、正在处理中的纠纷，我们会放慢节奏，等一个更完整的说法。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14" aria-labelledby="principles-heading">
          <div className="mb-6 border-b border-background-200 pb-4">
            <h2 id="principles-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              内容原则
            </h2>
            <p className="mt-2 font-body text-sm leading-6 text-foreground-600">
              这四条原则决定了我们发什么、怎么发，以及出错之后怎么办。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <article key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 font-body text-sm leading-7 text-foreground-600">{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-100 py-10 md:py-14" aria-labelledby="milestone-heading">
          <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
            <h2 id="milestone-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              发展理念
            </h2>
            <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-4">
              {milestones.map((item, index) => (
                <li key={item.title} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="absolute -top-3 left-5 rounded-full bg-primary-500 px-3 py-1 font-label text-[11px] text-background-50 dark:text-foreground-950">
                    {item.year}
                  </span>
                  <span className="font-heading text-3xl font-bold text-background-300">0{index + 1}</span>
                  <h3 className="mt-2 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 font-body text-sm leading-7 text-foreground-600">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14" aria-labelledby="voice-heading">
          <div className="mb-6 border-b border-background-200 pb-4">
            <h2 id="voice-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              读者怎么说
            </h2>
            <p className="mt-2 font-body text-sm leading-6 text-foreground-600">
              来自读者留言中的真实反馈，展示时已做匿名处理。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true"></i>
                <blockquote className="mt-3 flex-1 font-body text-sm leading-7 text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-semibold text-secondary-800">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block font-label text-sm text-foreground-900">{item.name}</span>
                    <span className="block font-label text-xs text-secondary-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="submit" className="w-full bg-background-100 py-10 md:py-14" aria-labelledby="contact-heading">
          <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <h2 id="contact-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
                  联系我们
                </h2>
                <p className="mt-3 font-body text-sm leading-7 text-foreground-600">
                  无论是提供线索、申请更正，还是内容合作，都可以通过下面的方式联系编辑部。
                  我们会在两个工作日内回复。
                </p>
                <ul className="mt-6 flex flex-col gap-4">
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-send-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block font-label text-xs text-secondary-500">投稿与更正邮箱</span>
                      <span className="mt-1 block font-label text-sm text-foreground-900">
                        tips@baoliao-today.example.com
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-time-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block font-label text-xs text-secondary-500">编辑部在线时间</span>
                      <span className="mt-1 block font-label text-sm text-foreground-900">
                        周一至周五 10:00 - 19:00
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-stack-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block font-label text-xs text-secondary-500">内容范围</span>
                      <span className="mt-1 block font-label text-sm text-foreground-900">
                        {channels.map((channel) => channel.name).join(" · ")}
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-7">
                <TipForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}